// @givmo/board — the officer's acts on the board's self-assessment (CBG-YR4-ASSESS-WEB; not drawn
// beyond s1 — the dashboard's dialogs, the derivation §3.3–§3.6): send, remind, close, withdraw, a
// paper answer sheet, and linking the board's priorities.
//
// ══ EVERY ACT SAYS WHAT WILL HAPPEN BEFORE IT HAPPENS ═══════════════════════════════════════════
//
// Sending and reminding name each person and show the email as it is sent (the approved words);
// closing says what the record holds — counts and comments without names — and that each person's own
// answers are then deleted, unless a legal hold covers the board (D-CBG-28); withdrawing says it
// records nothing and emails no one. Each act is the backend's, with its own refusals (a reminder
// inside a day, one no longer open, a sheet with no mark waiting for it), and a refusal shows the
// backend's own sentence. Every act is `board_record:approve` (contract C1); the page offers none to
// anyone else. Nothing here schedules anything.

import { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  isGivmoError,
  type BoardSelfAssessmentOut,
  type BoardSelfAssessmentPersonOut,
  type BoardSelfAssessmentResultsOut,
  type BoardMeetingSegmentOut,
  type BoardMotionOut,
  type OrgBoardWrittenConsentOut,
} from '@givmo/api-client';
import {
  Button,
  Checkbox,
  ErrorMessage,
  RadioTile,
  RadioTileGroup,
  Select,
  Skeleton,
  toast,
} from '@givmo/design-system';
import { useBoardOperations } from '../../board-runtime';
import { displayDate, displayLongDate } from '../../lib/display';
import { extractConsents } from '../consents/consents-decode';
import { extractMotions, extractSegments, isOpenSession } from '../in-meeting/in-meeting-decode';
import { displayMeetingStart, extractMeetings } from '../meetings/meetings-decode';
import { shortDay } from '../conflicts/coi-decode';
import { DialogFrame, EmailPreview, PartLabel, PersonCell } from '../conflicts/coi-parts';
import { AssessmentAnswerForm } from './assessment-answer-form';
import {
  EMPTY_DRAFT,
  answersIn,
  assessmentEmailLines,
  hasAnything,
  personCapacity,
  sheetsWaiting,
  type AnswerDraft,
} from './assessment-decode';
import { useRefreshSelfAssessments, useSelfAssessmentQuestions } from './assessment-queries';
import { assessmentWords as w } from './assessment-words';

const SECONDARY = 'font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary';
const QUIET = 'font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-tertiary';

function failure(caught: unknown): string {
  return isGivmoError(caught) && caught.message.trim().length > 0 ? caught.message : w.couldNotSave;
}

/** The people a send or a reminder names, each with their capacity and the mail's one difference. */
function NamedPeople({
  people,
  testId,
  when,
}: {
  people: readonly BoardSelfAssessmentPersonOut[];
  testId: string;
  when: (person: BoardSelfAssessmentPersonOut) => string | null;
}) {
  return (
    <ul className="flex flex-col divide-y divide-border-subtle rounded-md border border-border-subtle">
      {people.map((person) => (
        <li
          key={person.board_member_id}
          className="flex items-start justify-between gap-12 px-12 py-10"
          data-testid={`${testId}-${person.board_member_id}`}
        >
          <PersonCell
            name={person.display_name ?? ''}
            capacity={personCapacity(person)}
            note={person.joined_board ? undefined : w.includesInvitation}
          />
          <span className="shrink-0 font-sans text-s12 font-w400 tracking-givmo text-text-tertiary">
            {when(person)}
          </span>
        </li>
      ))}
    </ul>
  );
}

// ── Send (to someone not sent it yet) ───────────────────────────────────────────────────────────────

export function SendDialog({
  orgRef,
  assessment,
  people,
  organization,
  onClose,
}: {
  orgRef: string;
  assessment: BoardSelfAssessmentOut;
  /** Exactly who this send names: one row's person, or everyone the page read as sendable. */
  people: BoardSelfAssessmentPersonOut[];
  /** The organization's name, or null where it doesn't resolve (the mails' unnamed forms). */
  organization: string | null;
  onClose: () => void;
}) {
  const operations = useBoardOperations();
  const refresh = useRefreshSelfAssessments(orgRef);
  const [error, setError] = useState<string | null>(null);
  const first = people.find((person) => person.joined_board) ?? people[0];

  const send = useMutation({
    mutationFn: async () => {
      const { data } = await operations.officerSendBoardSelfAssessmentRequests({
        path: { org_ref: orgRef, assessment_id: assessment.id },
        // Exactly the people this dialog names, never `null`: someone who became sendable after the
        // page read gets no mail the officer didn't see named (all-or-nothing on the backend).
        body: { board_member_ids: people.map((person) => person.board_member_id) },
        throwOnError: true,
      });
      return data;
    },
    onSuccess: async (data) => {
      toast.success(w.sentTo(typeof data?.emails_sent === 'number' ? data.emails_sent : 0));
      await refresh();
      onClose();
    },
    onError: (caught) => setError(failure(caught)),
  });

  return (
    <DialogFrame
      title={w.sendTitle(people.map((person) => person.display_name ?? ''))}
      testId="assessment-send-dialog"
      onClose={onClose}
      footer={
        <>
          <Button
            appearance="saas"
            tone="secondary"
            onClick={onClose}
            testId="assessment-send-cancel"
          >
            {w.cancel}
          </Button>
          <Button
            appearance="saas"
            tone="primary"
            loading={send.isPending}
            onClick={() => {
              setError(null);
              send.mutate();
            }}
            testId="assessment-send-confirm"
          >
            {w.send}
          </Button>
        </>
      }
    >
      <p className={SECONDARY}>
        {people.length === 1 ? w.sendLeadOne(assessment.label) : w.sendLead(assessment.label)}
      </p>
      <NamedPeople
        people={people}
        testId="assessment-send"
        when={(person) =>
          person.joined_after_open_on
            ? w.joinedBoardOn(shortDay(person.joined_after_open_on))
            : null
        }
      />
      {first !== undefined ? (
        <EmailPreview
          testId="assessment-send-email"
          organization={organization ?? w.emailYourOrganization}
          subject={w.emailSubject(assessment.label)}
          button={w.emailButton}
          lines={assessmentEmailLines('request', {
            name: first.display_name ?? '',
            organization,
            label: assessment.label,
            dueOn: assessment.due_on,
            joined: first.joined_board,
          })}
        />
      ) : null}
      {error !== null ? <ErrorMessage testId="assessment-send-error">{error}</ErrorMessage> : null}
    </DialogFrame>
  );
}

// ── Remind ──────────────────────────────────────────────────────────────────────────────────────────

export function RemindDialog({
  orgRef,
  assessment,
  people,
  organization,
  onClose,
}: {
  orgRef: string;
  assessment: BoardSelfAssessmentOut;
  people: BoardSelfAssessmentPersonOut[];
  /** The organization's name, or null where it doesn't resolve (the mails' unnamed forms). */
  organization: string | null;
  onClose: () => void;
}) {
  const operations = useBoardOperations();
  const refresh = useRefreshSelfAssessments(orgRef);
  const [error, setError] = useState<string | null>(null);
  const first = people.find((person) => person.joined_board) ?? people[0];

  const remind = useMutation({
    mutationFn: async () => {
      const { data } = await operations.officerRemindBoardSelfAssessmentRecipients({
        path: { org_ref: orgRef, assessment_id: assessment.id },
        body: { board_member_ids: people.map((person) => person.board_member_id) },
        throwOnError: true,
      });
      return data;
    },
    onSuccess: async (data) => {
      toast.success(
        w.remindersSent(typeof data?.emails_sent === 'number' ? data.emails_sent : people.length),
      );
      await refresh();
      onClose();
    },
    onError: (caught) => setError(failure(caught)),
  });

  return (
    <DialogFrame
      title={w.remindTitle(people.map((person) => person.display_name ?? ''))}
      testId="assessment-remind-dialog"
      onClose={onClose}
      footer={
        <>
          <Button
            appearance="saas"
            tone="secondary"
            onClick={onClose}
            testId="assessment-remind-cancel"
          >
            {w.cancel}
          </Button>
          <Button
            appearance="saas"
            tone="primary"
            loading={remind.isPending}
            onClick={() => {
              setError(null);
              remind.mutate();
            }}
            testId="assessment-remind-send"
          >
            {w.sendReminders(people.length)}
          </Button>
        </>
      }
    >
      <p className={SECONDARY}>
        {people.length === 1 ? w.remindLeadOne(assessment.label) : w.remindLead(assessment.label)}
      </p>
      <NamedPeople
        people={people}
        testId="assessment-remind"
        when={(person) =>
          person.last_reminded_at
            ? w.lastReminded(shortDay(person.last_reminded_at))
            : person.first_sent_at
              ? w.sentOn(shortDay(person.first_sent_at))
              : null
        }
      />
      {first !== undefined ? (
        <EmailPreview
          testId="assessment-remind-email"
          organization={organization ?? w.emailYourOrganization}
          subject={w.emailSubject(assessment.label)}
          button={w.emailButton}
          lines={assessmentEmailLines('reminder', {
            name: first.display_name ?? '',
            organization,
            label: assessment.label,
            dueOn: assessment.due_on,
            joined: first.joined_board,
          })}
        />
      ) : null}
      {error !== null ? (
        <ErrorMessage testId="assessment-remind-error">{error}</ErrorMessage>
      ) : null}
    </DialogFrame>
  );
}

// ── Close ───────────────────────────────────────────────────────────────────────────────────────────

export function CloseDialog({
  orgRef,
  assessment,
  onClose,
  onClosed,
}: {
  orgRef: string;
  assessment: BoardSelfAssessmentOut;
  onClose: () => void;
  /** Where the page goes once the results are recorded: that assessment's results. */
  onClosed: (assessmentId: string) => void;
}) {
  const operations = useBoardOperations();
  const refresh = useRefreshSelfAssessments(orgRef);
  const [error, setError] = useState<string | null>(null);

  const close = useMutation({
    mutationFn: async () => {
      await operations.officerCloseBoardSelfAssessment({
        path: { org_ref: orgRef, assessment_id: assessment.id },
        throwOnError: true,
      });
    },
    onSuccess: async () => {
      toast.success(w.closed(assessment.label));
      await refresh();
      onClosed(assessment.id);
    },
    onError: (caught) => setError(failure(caught)),
  });

  return (
    <DialogFrame
      title={w.close(assessment.label)}
      testId="assessment-close-dialog"
      width={480}
      onClose={onClose}
      footer={
        <>
          <Button
            appearance="saas"
            tone="secondary"
            onClick={onClose}
            testId="assessment-close-cancel"
          >
            {w.cancel}
          </Button>
          <Button
            appearance="saas"
            tone="primary"
            loading={close.isPending}
            onClick={() => {
              setError(null);
              close.mutate();
            }}
            testId="assessment-close-confirm"
          >
            {w.closeAndRecord}
          </Button>
        </>
      }
    >
      <p className="font-sans text-s14 font-w600 leading-h15 tracking-givmo text-text-strong">
        {w.closeCount(assessment.answered_count, assessment.asked_count)}
      </p>
      <p className={SECONDARY}>{w.closeRecord}</p>
      {assessment.answered_count < 3 ? (
        <p className={SECONDARY} data-testid="assessment-close-under-three">
          {w.closeUnderThree}
        </p>
      ) : null}
      <p className={SECONDARY}>{w.closeNoMore}</p>
      {error !== null ? <ErrorMessage testId="assessment-close-error">{error}</ErrorMessage> : null}
    </DialogFrame>
  );
}

// ── Withdraw (before anyone has answered) ───────────────────────────────────────────────────────────

export function WithdrawDialog({
  orgRef,
  assessment,
  onClose,
}: {
  orgRef: string;
  assessment: BoardSelfAssessmentOut;
  onClose: () => void;
}) {
  const operations = useBoardOperations();
  const refresh = useRefreshSelfAssessments(orgRef);
  const [error, setError] = useState<string | null>(null);

  const withdraw = useMutation({
    mutationFn: async () => {
      await operations.officerCancelBoardSelfAssessment({
        path: { org_ref: orgRef, assessment_id: assessment.id },
        throwOnError: true,
      });
    },
    onSuccess: async () => {
      toast.success(w.withdrawn(assessment.label));
      await refresh();
      onClose();
    },
    onError: (caught) => setError(failure(caught)),
  });

  return (
    <DialogFrame
      title={w.withdraw(assessment.label)}
      testId="assessment-withdraw-dialog"
      width={440}
      onClose={onClose}
      footer={
        <>
          <Button
            appearance="saas"
            tone="secondary"
            onClick={onClose}
            testId="assessment-withdraw-cancel"
          >
            {w.cancel}
          </Button>
          <Button
            appearance="saas"
            tone="primary"
            loading={withdraw.isPending}
            onClick={() => {
              setError(null);
              withdraw.mutate();
            }}
            testId="assessment-withdraw-confirm"
          >
            {w.withdrawButton}
          </Button>
        </>
      }
    >
      <p className={SECONDARY}>{w.withdrawLead}</p>
      {error !== null ? (
        <ErrorMessage testId="assessment-withdraw-error">{error}</ErrorMessage>
      ) : null}
    </DialogFrame>
  );
}

// ── A paper answer sheet, entered unnamed (marks before sheets) ────────────────────────────────────

export function PaperSheetDialog({
  orgRef,
  assessment,
  onClose,
}: {
  orgRef: string;
  assessment: BoardSelfAssessmentOut;
  onClose: () => void;
}) {
  const operations = useBoardOperations();
  const refresh = useRefreshSelfAssessments(orgRef);
  const questionsQuery = useSelfAssessmentQuestions(orgRef, assessment.id);
  const questions = questionsQuery.data ?? null;
  const [draft, setDraft] = useState<AnswerDraft>(EMPTY_DRAFT);
  const [error, setError] = useState<string | null>(null);
  const waiting = sheetsWaiting(assessment);
  const marks = assessment.people.filter((person) => Boolean(person.handed_in_on_paper_at)).length;

  const record = useMutation({
    mutationFn: async () => {
      if (questions === null) throw new Error(w.couldNotSave);
      await operations.officerRecordBoardSelfAssessmentPaperSheet({
        path: { org_ref: orgRef, assessment_id: assessment.id },
        body: answersIn(draft, questions),
        throwOnError: true,
      });
    },
    onSuccess: async () => {
      toast.success(w.sheetRecorded);
      await refresh();
      onClose();
    },
    onError: (caught) => setError(failure(caught)),
  });

  return (
    <DialogFrame
      title={w.recordSheet}
      testId="assessment-sheet-dialog"
      width={720}
      onClose={onClose}
      footer={
        <>
          <Button
            appearance="saas"
            tone="secondary"
            onClick={onClose}
            testId="assessment-sheet-cancel"
          >
            {w.cancel}
          </Button>
          <Button
            appearance="saas"
            tone="primary"
            disabled={questions === null}
            loading={record.isPending}
            onClick={() => {
              setError(null);
              if (!hasAnything(draft)) {
                setError(w.answerAtLeastOne);
                return;
              }
              record.mutate();
            }}
            testId="assessment-sheet-record"
          >
            {w.recordThisSheet}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-6">
        <p className={SECONDARY}>{w.sheetLead}</p>
        {waiting > 0 ? <p className={QUIET}>{w.sheetWaiting(waiting)}</p> : null}
        {marks === 1 ? (
          <p className={QUIET} data-testid="assessment-sheet-inferable">
            {w.sheetInferable}
          </p>
        ) : null}
      </div>
      {questionsQuery.isPending ? (
        <Skeleton className="h-84 w-full rounded-md" />
      ) : questions === null ? (
        <ErrorMessage tone="muted">{w.questionsUnavailable}</ErrorMessage>
      ) : (
        <AssessmentAnswerForm
          questions={questions}
          draft={draft}
          onChange={setDraft}
          disabled={record.isPending}
          framing="sections"
          testId="assessment-sheet"
        />
      )}
      {error !== null ? <ErrorMessage testId="assessment-sheet-error">{error}</ErrorMessage> : null}
    </DialogFrame>
  );
}

// ── The board's priorities: the board's own act, linked ───────────────────────────────────────────

type Kind = 'motion' | 'consent';

/** A motion the board recorded as carried: the only kind a priorities link admits. */
function carried(motion: BoardMotionOut): boolean {
  return motion.status === 'voted' && motion.outcome === 'carried';
}

/**
 * A motion the board made in the open: in no session, or in an open one. The results are every board
 * reader's, so a closed session's act can't be named as their priorities (contract §7, C23; the
 * backend refuses it). Stated positively, as `isOpenSession` is, so a session this officer isn't
 * shown, or a kind a later backend adds, is never taken for an open one.
 */
function madeInTheOpen(
  motion: BoardMotionOut,
  segments: readonly BoardMeetingSegmentOut[],
): boolean {
  if (!motion.segment_id) return true;
  return isOpenSession(segments.find((segment) => segment.id === motion.segment_id));
}

export function PrioritiesDialog({
  orgRef,
  assessmentId,
  results,
  onClose,
}: {
  orgRef: string;
  assessmentId: string;
  results: BoardSelfAssessmentResultsOut;
  onClose: () => void;
}) {
  const operations = useBoardOperations();
  const refresh = useRefreshSelfAssessments(orgRef);
  const linked = results.priorities ?? null;
  const [kind, setKind] = useState<Kind>(linked?.kind === 'consent' ? 'consent' : 'motion');
  const [meetingId, setMeetingId] = useState<string | null>(linked?.meeting_id ?? null);
  const [motionId, setMotionId] = useState<string | null>(linked?.motion_id ?? null);
  const [consentId, setConsentId] = useState<string | null>(linked?.consent_id ?? null);
  const [named, setNamed] = useState<ReadonlySet<string>>(
    () => new Set((linked?.statements ?? []).map((statement) => statement.statement_id)),
  );
  const [error, setError] = useState<string | null>(null);

  const meetingsQuery = useQuery({
    ...operations.officerListBoardMeetingsOptions({ path: { org_ref: orgRef } }),
    enabled: kind === 'motion',
    select: (body: unknown) =>
      extractMeetings(body)
        .filter((meeting) => meeting.status !== 'cancelled')
        .sort((a, b) => (b.starts_at ?? '').localeCompare(a.starts_at ?? '')),
  });
  const motionsQuery = useQuery({
    ...operations.officerListBoardMotionsOptions({
      path: { org_ref: orgRef, meeting_id: meetingId ?? '' },
    }),
    enabled: kind === 'motion' && meetingId !== null,
    select: (body: unknown) => extractMotions(body).filter(carried),
  });
  const segmentsQuery = useQuery({
    ...operations.officerListBoardMeetingSegmentsOptions({
      path: { org_ref: orgRef, meeting_id: meetingId ?? '' },
    }),
    enabled: kind === 'motion' && meetingId !== null,
    select: extractSegments,
  });
  const consentsQuery = useQuery({
    queryKey: ['board-self-assessment-consents', orgRef],
    enabled: kind === 'consent',
    queryFn: async (): Promise<OrgBoardWrittenConsentOut[]> => {
      const { data } = await operations.officerListBoardWrittenConsents({
        path: { org_ref: orgRef },
        throwOnError: true,
      });
      return extractConsents(data).filter((consent) => consent.status === 'executed');
    },
  });

  const statements = useMemo(
    () => (results.groups ?? []).flatMap((group) => group.statements),
    [results.groups],
  );

  const link = useMutation({
    mutationFn: async () => {
      await operations.officerLinkBoardSelfAssessmentPriorities({
        path: { org_ref: orgRef, assessment_id: assessmentId },
        body:
          kind === 'motion'
            ? { motion_id: motionId, consent_id: null, statement_ids: [...named] }
            : { motion_id: null, consent_id: consentId, statement_ids: [...named] },
        throwOnError: true,
      });
    },
    onSuccess: async () => {
      toast.success(w.linked);
      await refresh();
      onClose();
    },
    onError: (caught) => setError(failure(caught)),
  });

  const chosen = kind === 'motion' ? motionId !== null : consentId !== null;
  const meetings = meetingsQuery.data ?? [];
  const segments = segmentsQuery.data ?? [];
  const motions = (motionsQuery.data ?? []).filter((motion) => madeInTheOpen(motion, segments));
  const consents = consentsQuery.data ?? [];

  return (
    <DialogFrame
      title={w.recordPriorities}
      lead={w.linkLead}
      testId="assessment-priorities-dialog"
      width={640}
      onClose={onClose}
      footer={
        <>
          <Button
            appearance="saas"
            tone="secondary"
            onClick={onClose}
            testId="assessment-priorities-cancel"
          >
            {w.cancel}
          </Button>
          <Button
            appearance="saas"
            tone="primary"
            disabled={!chosen}
            loading={link.isPending}
            onClick={() => {
              setError(null);
              link.mutate();
            }}
            testId="assessment-priorities-link"
          >
            {w.linkIt}
          </Button>
        </>
      }
    >
      <RadioTileGroup
        value={kind}
        onValueChange={(next) => setKind(next === 'consent' ? 'consent' : 'motion')}
        aria-label={w.recordPriorities}
        className="sm:flex-row sm:gap-x-28"
        testId="assessment-priorities-kind"
      >
        <RadioTile value="motion" label={w.aMotion} testId="assessment-priorities-motion" />
        <RadioTile value="consent" label={w.aConsent} testId="assessment-priorities-consent" />
      </RadioTileGroup>

      {kind === 'motion' ? (
        <>
          <Select
            label={w.meetingField}
            appearance="saas"
            placeholder={w.chooseMeeting}
            value={meetingId}
            disabled={meetingsQuery.isPending}
            onValueChange={(next) => {
              setMeetingId(next);
              setMotionId(null);
            }}
            options={meetings.map((meeting) => ({
              value: meeting.id,
              label: `${meeting.title ?? ''} · ${displayMeetingStart(meeting)}`,
            }))}
            testId="assessment-priorities-meeting"
          />
          {meetingId === null ? null : motionsQuery.isPending || segmentsQuery.isPending ? (
            <Skeleton className="h-48 w-full rounded-md" />
          ) : motions.length === 0 ? (
            <p className={QUIET}>{w.noCarriedMotions}</p>
          ) : (
            <section className="flex flex-col gap-8">
              <PartLabel>{w.motionField}</PartLabel>
              <RadioTileGroup
                value={motionId ?? ''}
                onValueChange={setMotionId}
                aria-label={w.motionField}
                testId="assessment-priorities-motions"
              >
                {motions.map((motion) => (
                  <RadioTile
                    key={motion.id}
                    value={motion.id}
                    label={motion.motion_text}
                    testId={`assessment-priorities-motion-${motion.id}`}
                  />
                ))}
              </RadioTileGroup>
            </section>
          )}
        </>
      ) : consentsQuery.isPending ? (
        <Skeleton className="h-48 w-full rounded-md" />
      ) : consents.length === 0 ? (
        <p className={QUIET}>{w.noExecutedConsents}</p>
      ) : (
        <section className="flex flex-col gap-8">
          <PartLabel>{w.consentField}</PartLabel>
          <RadioTileGroup
            value={consentId ?? ''}
            onValueChange={setConsentId}
            aria-label={w.consentField}
            testId="assessment-priorities-consents"
          >
            {consents.map((consent) => (
              <RadioTile
                key={consent.id}
                value={consent.id}
                label={consent.version?.title ?? ''}
                description={
                  displayLongDate(consent.executed_at ?? null) ??
                  displayDate(consent.consent_date ?? null)
                }
                testId={`assessment-priorities-consent-${consent.id}`}
              />
            ))}
          </RadioTileGroup>
        </section>
      )}

      {statements.length > 0 ? (
        <section className="flex flex-col gap-8" data-testid="assessment-priorities-statements">
          <PartLabel>{w.statementsField}</PartLabel>
          <ul className="flex flex-col gap-8">
            {statements.map((statement) => (
              <li key={statement.statement_id}>
                <label className="flex cursor-pointer items-start gap-10">
                  <Checkbox
                    appearance="saas"
                    checked={named.has(statement.statement_id)}
                    onCheckedChange={(on) =>
                      setNamed((current) => {
                        const next = new Set(current);
                        if (on) next.add(statement.statement_id);
                        else next.delete(statement.statement_id);
                        return next;
                      })
                    }
                    className="mt-2"
                    testId={`assessment-priorities-statement-${statement.statement_id}`}
                  />
                  <span className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-strong">
                    {statement.text}
                  </span>
                </label>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {error !== null ? (
        <ErrorMessage testId="assessment-priorities-error">{error}</ErrorMessage>
      ) : null}
    </DialogFrame>
  );
}
